import { useScrollIntoView } from 'src/hooks/useScrollIntoView'
import { Link } from '.'
import { Box, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material'
import { HorizontalRuleIcon, LocationOnIcon } from 'src/assets/icons'
import { useState } from 'react'

type StatesWeServiceProps = {
    link: Link
}

const StatesWeService = ({ link }: StatesWeServiceProps) => {
    const theme = useTheme()
    const smMedium = useMediaQuery(theme.breakpoints.down(1100))
    const mobile = useMediaQuery(theme.breakpoints.down(900))
    const pins = [
        {
            state: 'New York',
            offsetX: mobile ? 66 : 83,
            offsetY: mobile ? 30 : 34
        },
        {
            state: 'New Jersey',
            offsetX: mobile ? 71 : 84,
            offsetY: 40
        },
        {
            state: 'Florida',
            offsetX: mobile ? 50 : 72,
            offsetY: 80
        },
        {
            state: 'Pennsylvania',
            offsetX: mobile ? 58 : 76,
            offsetY: 38
        },
        {
            state: 'Massachusetts',
            offsetX: mobile ? 78 : 88,
            offsetY: 35
        },
        {
            state: 'North Carolina',
            offsetX: mobile ? 56 : 74,
            offsetY: 56
        }
    ] as const
    const [hovering, setHovering] = useState<(typeof pins)[number]['state'] | null>(null)
    const [showText, setShowText] = useState(false)
    useScrollIntoView(link)
    const mapImage = mobile
        ? 'https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_900/scs/site/map-mobile_va9fs7.png'
        : 'https://res.cloudinary.com/dae9ughdn/image/upload/f_webp,q_auto,w_1400/scs/site/map_tmombh.png'

    return (
        <Box
            ref={link.componentRef}
            className="px-28 pt-20 pb-24  ltb_1300:px-12 tbmd:px-5"
            sx={{ backgroundImage: !mobile ? '' : 'url(/images/lines.png)', backgroundPosition: 'right', backgroundRepeat: 'no-repeat' }}
        >
            <Box className="flex items-center gap-3">
                <HorizontalRuleIcon className="text-[#4E96C4]" />
                <Typography className="font-[Barlow] text-[15px] font-bold uppercase tracking-[1.95px] text-[#4E96C4]">6 Locations</Typography>
                <HorizontalRuleIcon className="text-[#4E96C4]" />
            </Box>
            <Typography className="mb-8 font-[Barlow] text-[40px] font-bold text-[#364152]">States We Service</Typography>
            <Box>
                <Box className="flex gap-8 tbmd:flex-col">
                    <Box className="relative">
                        <img
                            className="h-auto max-w-full rounded-3xl border-[5px] border-solid border-[#E3E8EF]"
                            src={mapImage}
                            alt="map"
                            loading="lazy"
                            decoding="async"
                            width={mobile ? 1107 : 2014}
                            height={mobile ? 1249 : 1254}
                        />
                        {pins.map((pin) => {
                            const isHovering = hovering === pin.state
                            return (
                                <IconButton
                                    key={pin.state}
                                    onMouseEnter={() => {
                                        setTimeout(() => setShowText(true), 500)
                                        setHovering(pin.state)
                                    }}
                                    onMouseLeave={() => {
                                        setHovering(null)
                                        setShowText(false)
                                    }}
                                    aria-label={pin.state}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'white'
                                        },
                                        p: smMedium ? '4px' : '8px',
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        left: `${pin.offsetX}%`,
                                        top: `${pin.offsetY}%`,
                                        position: 'absolute',
                                        backgroundColor: 'white',
                                        borderRadius: '50px',
                                        width: isHovering ? '165px' : '40px',
                                        transition: 'width  0.5s ease-out',
                                        zIndex: isHovering ? 50 : 'initial'
                                    }}
                                >
                                    <LocationOnIcon className="text-[#346791]" fontSize={smMedium ? 'small' : 'medium'} />
                                    {isHovering && showText && (
                                        <Typography sx={{ fontSize: mobile ? '12px' : smMedium ? '14px' : '16px' }} className="ml-1 text-[#346791]">
                                            {pin.state}
                                        </Typography>
                                    )}
                                </IconButton>
                            )
                        })}
                    </Box>
                    <Box
                        sx={{ backgroundImage: mobile ? '' : 'url(/images/lines.png)' }}
                        className="flex flex-col gap-4 tbmd:flex-row tbmd:flex-wrap tbmd:gap-6 tbmd:gap-y-3"
                    >
                        {pins.map((pin) => (
                            <Typography className="text-[#364152]" key={pin.state}>
                                {pin.state}
                            </Typography>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default StatesWeService
